import React, { useState, useCallback, useEffect } from "react";
import { Flame, Sparkles, TrendingUp, AlertTriangle, XCircle, Loader2, Shield } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { Turnstile } from "@marsidev/react-turnstile";
import ScoreDisplay from "@/components/ScoreDisplay";
import BreakdownChart from "@/components/BreakdownChart";
import FeedbackPanel from "@/components/FeedbackPanel";
import RewriteSuggestion from "@/components/RewriteSuggestion";
import SpamFlags from "@/components/SpamFlags";
import LoadingState from "@/components/LoadingState";

// API endpoint - uses Express proxy for security (BFF pattern)
// The proxy handles token validation and hides the n8n webhook URL from browser
const API_URL = '/api/dispatch';

// Access token from environment (set in Railway build vars or .env)
const ACCESS_TOKEN = process.env.REACT_APP_ACCESS_TOKEN || '';

// Client-side content limits to match backend guardrails
const MIN_LENGTH = 10;
const MAX_LENGTH = 4000;

// Cloudflare Turnstile site key (public, set in Railway build vars)
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

// v1.4.1: Decode HTML entities from backend sanitization
// The backend sanitizeForOutput encodes special chars for XSS protection,
// but React's JSX rendering doesn't interpret HTML entities as HTML
const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== 'string') return str;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
};

const platforms = [
  { value: "linkedin", label: "LinkedIn", icon: "💼" },
  { value: "twitter", label: "Twitter/X", icon: "𝕏" },
  { value: "instagram", label: "Instagram", icon: "📷" },
  { value: "facebook", label: "Facebook", icon: "👥" },
  { value: "generic", label: "Generic", icon: "📝" },
];

export const ViralPostGrader = () => {
  const [content, setContent] = useState("");
  const [platform, setPlatform] = useState("linkedin");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [brandingLoaded, setBrandingLoaded] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0); // For resetting widget

  useEffect(() => {
    // Trigger branding animation on mount
    const timer = setTimeout(() => setBrandingLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Prevent duplicate API calls while grading is in progress
  const isGradingRef = React.useRef(false);

  const gradePost = useCallback(async () => {
    // Prevent duplicate calls
    if (isGradingRef.current) {
      return;
    }
    isGradingRef.current = true;

    const trimmed = content.trim();
    if (!trimmed) {
      toast.error("Please enter some content to grade");
      isGradingRef.current = false;
      return;
    }
    if (trimmed.length < MIN_LENGTH) {
      toast.error(`Content must be at least ${MIN_LENGTH} characters`);
      isGradingRef.current = false;
      return;
    }
    if (trimmed.length > MAX_LENGTH) {
      toast.error(`Content must be under ${MAX_LENGTH} characters`);
      isGradingRef.current = false;
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000); // 20s client timeout

      const requestBody = {
        content: trimmed,
        platform,
        tone: "professional",
        source: "emergent",
        // Include Turnstile token if available (for bot defense)
        ...(turnstileToken && { turnstile_token: turnstileToken }),
      };

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(ACCESS_TOKEN && { "X-Access-Token": ACCESS_TOKEN }),
        },
        signal: controller.signal,
        body: JSON.stringify(requestBody),
      });
      clearTimeout(timeoutId);

      // Read response as text first, then parse as JSON
      // This is more robust than response.json() for error handling
      let textBody;
      let data;
      
      try {
        if (response.bodyUsed) {
          throw new Error("Response body was consumed by an external script.");
        }
        
        textBody = await response.text();
        data = JSON.parse(textBody);
      } catch (parseError) {
        console.error("Failed to parse JSON response:", textBody || "(body not available)");
        throw new Error(parseError.message.includes("external script") ? parseError.message : "Invalid response from server");
      }

      if (!response.ok) {
        const errorMsg = data.error?.message || data.error || data.message || (typeof data === 'string' ? data : null) || "Failed to grade your post";
        throw new Error(errorMsg);
      }

      // Handle the response - recursively find object with overall_score (handles any n8n/BFF nesting)
      const findGradingData = (obj, depth = 0) => {
        if (depth > 6 || !obj || typeof obj !== 'object') return null;
        if (obj.overall_score !== undefined && (typeof obj.overall_score === 'number' || typeof obj.overall_score === 'string')) return obj;
        if (obj.breakdown && typeof obj.breakdown === 'object') return obj;
        if (Array.isArray(obj)) {
          for (const item of obj) {
            const found = findGradingData(item, depth + 1);
            if (found) return found;
          }
        } else {
          for (const key of ['data', 'json', 'body', 'result', 'output']) {
            const found = findGradingData(obj[key], depth + 1);
            if (found) return found;
          }
        }
        return null;
      };
      let resultData = findGradingData(data) ?? data?.data ?? data;
      const isEmptyResponse = !resultData || (typeof resultData === 'object' && Object.keys(resultData).length === 0);
      if (isEmptyResponse || (resultData?.overall_score === undefined && !resultData?.breakdown)) {
        throw new Error('Backend returned empty or invalid response. Please try again or verify the service configuration.');
      }

      setResult(resultData);
      toast.success("Post analyzed successfully!");
    } catch (err) {
      console.error("Error grading post:", err);
      setError(err.message || "Something went wrong. Please try again.");
      toast.error("Failed to analyze post");
    } finally {
      setIsLoading(false);
      // Reset Turnstile widget for next submission
      setTurnstileToken(null);
      setTurnstileKey(prev => prev + 1);
      isGradingRef.current = false;
    }
  }, [content, platform, turnstileToken]);

  const handleRetry = () => {
    setError(null);
    gradePost();
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Branding Header */}
        <div className="flex items-center gap-2 mb-2">
          <span 
            className={`text-xl transition-all duration-500 ${brandingLoaded ? 'animate-pulse-once opacity-100' : 'opacity-0'}`}
            style={{ animationIterationCount: 1 }}
          >
            ⚡
          </span>
          <span 
            className={`text-sm font-medium tracking-tight transition-all duration-700 ease-out ${brandingLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}
            style={{ color: '#4b5563' }}
          >
            Jarvis Sovereign System
          </span>
        </div>

        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
              Viral Post Grader
            </h1>
            <span className="text-3xl" role="img" aria-label="fire">🔥</span>
          </div>
          <p className="text-muted-foreground text-base sm:text-lg">
            Score your content before you post
          </p>
        </div>

        {/* Input Card */}
        <Card className="border border-border shadow-md">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Your Content
            </CardTitle>
            <CardDescription>
              Paste your post and select the platform to get instant feedback
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Textarea
                placeholder="Paste your post content here..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="min-h-[120px] resize-y text-base leading-relaxed focus:ring-2 focus:ring-primary/20"
                aria-label="Post content"
                maxLength={MAX_LENGTH}
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{content.length} / {MAX_LENGTH} characters</span>
                {content.length > MAX_LENGTH && (
                  <span className="text-destructive font-medium">Over limit</span>
                )}
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden border border-input">
                <div
                  className={`h-full transition-all ${
                    content.length > MAX_LENGTH
                      ? 'bg-destructive'
                      : content.length >= 0.9 * MAX_LENGTH
                        ? 'bg-amber-500'
                        : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min((content.length / MAX_LENGTH) * 100, 100)}%` }}
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger className="w-full sm:w-[180px]" aria-label="Select platform">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  {platforms.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      <span className="flex items-center gap-2">
                        <span>{p.icon}</span>
                        <span>{p.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                onClick={gradePost}
                disabled={
                  isLoading ||
                  !content.trim() ||
                  content.trim().length < MIN_LENGTH ||
                  content.trim().length > MAX_LENGTH ||
                  (TURNSTILE_SITE_KEY && !turnstileToken)
                }
                className="flex-1 sm:flex-none bg-primary text-primary-foreground hover:bg-primary/90 font-medium h-10"
                aria-label="Grade my post"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="ml-2">Analyzing...</span>
                  </>
                ) : (
                  <>
                    <TrendingUp className="h-4 w-4" />
                    <span className="ml-2">Grade My Post</span>
                  </>
                )}
              </Button>
            </div>
            
            {/* Cloudflare Turnstile - Bot Defense (only shown if configured) */}
            {TURNSTILE_SITE_KEY && (
              <div className="flex items-center justify-center gap-2 pt-2">
                <Turnstile
                  key={turnstileKey}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onError={() => {
                    setTurnstileToken(null);
                    toast.error("Bot verification failed. Please try again.");
                  }}
                  onExpire={() => setTurnstileToken(null)}
                  options={{
                    theme: 'light',
                    size: 'normal'
                  }}
                />
              </div>
            )}
            
            {/* Security indicator */}
            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground pt-1">
              <Shield className="h-3 w-3" />
              <span>Protected by rate limiting{TURNSTILE_SITE_KEY ? ' & bot defense' : ''}</span>
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {isLoading && <LoadingState />}

        {/* Error State */}
        {error && (
          <Alert variant="destructive" className="animate-fade-in-up border-destructive/50 bg-destructive/10">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className="font-semibold">Something went wrong</AlertTitle>
            <AlertDescription className="mt-2">
              <p className="text-sm mb-3">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetry}
                className="text-destructive border-destructive/30 hover:bg-destructive/10"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Results */}
        {result && !isLoading && (
          <div className="space-y-4 animate-fade-in-up">
            {/* Score Display */}
            <ScoreDisplay 
              score={result.overall_score} 
              verdict={result.verdict} 
            />

            {/* Spam Flags - v1.1.0: Show warning if spam detected */}
            {result.spam_flags && result.spam_flags.length > 0 && (
              <SpamFlags flags={result.spam_flags} />
            )}

            {/* Platform Fit Analysis - v1.4.0 */}
            {result.platform_fit && (
              <Card className="border-blue-200 bg-blue-50/50 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2 text-blue-700">
                    <span>🎯</span>
                    Platform Fit
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {decodeHtmlEntities(result.platform_fit)}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Breakdown Chart */}
            {result.breakdown && (
              <BreakdownChart breakdown={result.breakdown} />
            )}

            {/* Feedback Panel */}
            {result.feedback && (
              <FeedbackPanel feedback={result.feedback} />
            )}

            {/* Rewrite Suggestion */}
            {result.rewrite_suggestion && (
              <RewriteSuggestion suggestion={result.rewrite_suggestion} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViralPostGrader;
